import type { Dataset, SessionUser } from '../../shared/types';
import { useAuth } from '../auth/AuthProvider';
import { RANGE_PILLS, TABS, type Range, type TabId } from '../lib/compute';
import { Filter } from './Filter';
import { Logo } from './Logo';

interface HeaderProps {
  user: SessionUser | null;
  data: Dataset;
  tab: TabId;
  range: Range;
  selChannels: string[];
  selCountries: string[];
  openFilter: 'channels' | 'countries' | null;
  onTab: (t: TabId) => void;
  onRange: (r: Range) => void;
  onToggleFilter: (which: 'channels' | 'countries') => void;
  onCloseFilter: () => void;
  onToggleChannel: (name: string) => void;
  onToggleCountry: (name: string) => void;
  onAllChannels: () => void;
  onAllCountries: () => void;
  onSignOut: () => void;
  onOpenTeam: () => void;
  countryOptions: string[];
  from: string;
  to: string;
  minDate: string;
  maxDate: string;
  onFrom: (v: string) => void;
  onTo: (v: string) => void;
}

export function Header(props: HeaderProps) {
  const { user, data } = props;
  const { isAdmin } = useAuth();
  return (
    <header className="cw-header">
      <div className="cw-header-inner">
        {/* Row 1 — brand · tabs (centred on desktop) · user */}
        <div className="cw-row-top">
          <button className="cw-brand" onClick={() => props.onTab('cockpit')} title="Zum Cockpit" aria-label="Zum Cockpit">
            <Logo className="cw-brand-logo" />
            <span className="cw-brand-tag">Performance</span>
          </button>

          <nav className="cw-nav">
            {TABS.map((t) => (
              <button
                key={t.id}
                className={`cw-tab${t.id === props.tab ? ' is-active' : ''}`}
                onClick={() => props.onTab(t.id)}
              >
                {t.label}
              </button>
            ))}
          </nav>

          <div className="cw-user">
            {isAdmin && (
              <button className="cw-signout" title="Team-Zugriff verwalten" onClick={props.onOpenTeam}>
                Team
              </button>
            )}
            <div className="cw-avatar" title={user?.email}>
              {user?.picture ? <img src={user.picture} alt="" /> : user?.initials ?? 'CW'}
            </div>
            <button className="cw-signout" title="Abmelden" onClick={props.onSignOut}>
              Abmelden
            </button>
          </div>
        </div>

        {/* Row 2 — range (left) · filters (right). Stacks + centres on mobile. */}
        <div className="cw-row-bottom">
          <div className="cw-rangewrap">
            <div className="cw-rangebar">
              {RANGE_PILLS.map((r) => (
                <button
                  key={r}
                  className={`cw-range-btn${r === props.range ? ' is-active' : ''}`}
                  onClick={() => props.onRange(r)}
                >
                  {r}
                </button>
              ))}
            </div>
          </div>

          <div className="cw-filterbar">
            <span className="cw-filter-label">Filter</span>
            <Filter
              label="Kanäle"
              options={data.channels.map((c) => ({ name: c.name, color: c.color }))}
              selected={props.selChannels}
              total={data.channels.length}
              isOpen={props.openFilter === 'channels'}
              onToggleOpen={() => props.onToggleFilter('channels')}
              onClose={props.onCloseFilter}
              onToggleOption={props.onToggleChannel}
              onToggleAll={props.onAllChannels}
            />
            <Filter
              label="Länder"
              options={props.countryOptions.map((c) => ({ name: c }))}
              selected={props.selCountries}
              total={props.countryOptions.length}
              isOpen={props.openFilter === 'countries'}
              scroll
              onToggleOpen={() => props.onToggleFilter('countries')}
              onClose={props.onCloseFilter}
              onToggleOption={props.onToggleCountry}
              onToggleAll={props.onAllCountries}
            />
          </div>

          <div className="cw-daterow">
            <input type="date" className="cw-date" value={props.from} min={props.minDate} max={props.to || props.maxDate} onChange={(e) => props.onFrom(e.target.value)} />
            <span className="cw-date-sep">–</span>
            <input type="date" className="cw-date" value={props.to} min={props.from || props.minDate} max={props.maxDate} onChange={(e) => props.onTo(e.target.value)} />
          </div>
        </div>
      </div>
    </header>
  );
}
